import { useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { pushBillingCycleToggled } from "@/lib/analytics";
import type { BillingCycle } from "@/lib/subscription";

interface BillingToggleProps {
  initialValue?: BillingCycle;
  onChange?: (value: BillingCycle) => void;
  className?: string;
}

export const BillingToggle = ({ initialValue = "monthly", onChange, className }: BillingToggleProps) => {
  const [selected, setSelected] = useState<BillingCycle>(initialValue);

  const handleToggle = (value: BillingCycle) => {
    if (value === selected) return;
    pushBillingCycleToggled({ from: selected, to: value, location: "plans_page" });
    setSelected(value);
    onChange?.(value);
  };

  return (
    <div className={cn("flex items-center justify-center p-4", className)}>
      <div
        className="relative flex items-center bg-slate-200/70 rounded-full p-1 h-[48px] w-[260px] cursor-pointer overflow-hidden"
        role="radiogroup"
        aria-label="Select billing period"
      >
        <motion.div
          className="absolute h-[40px] bg-slate-900 rounded-full z-10"
          initial={false}
          animate={{
            width: selected === "monthly" ? 97 : 151,
            x: selected === "monthly" ? 0 : 101,
          }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        />

        <button
          onClick={() => handleToggle("monthly")}
          className="relative z-20 flex items-center justify-center h-full transition-colors duration-200 focus:outline-none w-[97px] px-5"
          aria-checked={selected === "monthly"}
          role="radio"
        >
          <span
            className={cn(
              "text-sm font-semibold whitespace-nowrap font-sans",
              selected === "monthly" ? "text-white" : "text-slate-500",
            )}
          >
            Monthly
          </span>
        </button>

        <button
          onClick={() => handleToggle("annual")}
          className="relative z-20 flex items-center justify-center h-full gap-2 transition-colors duration-200 focus:outline-none w-[151px] pl-5 pr-3"
          aria-checked={selected === "annual"}
          role="radio"
        >
          <span
            className={cn(
              "text-sm font-semibold whitespace-nowrap font-sans",
              selected === "annual" ? "text-white" : "text-slate-500",
            )}
          >
            Annual
          </span>
          <div className="flex items-center justify-center bg-accent px-1.5 py-0.5 rounded-full min-w-[55px] h-[20px]">
            <span className="text-[10px] font-bold text-accent-foreground uppercase tracking-tight whitespace-nowrap font-sans">
              SAVE 20%
            </span>
          </div>
        </button>
      </div>
    </div>
  );
};
